import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, TrendingDown, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { PRICING, TESTIMONIALS } from "@/data";

export const Pricing = ({ onBookDemo }) => (
  <section id="pricing" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <Reveal>
      <Kicker num="08">Pricing</Kicker>
      <SectionHeading className="mt-6">Simple pricing.</SectionHeading>
      <p className="mt-5 max-w-xl text-lg text-slate-500">
        Start with what you need. Add modules as your operation grows — no rebuild, no migration.
      </p>
    </Reveal>

    <div className="mt-14 grid gap-4 lg:grid-cols-3">
      {PRICING.map((p, i) => (
        <Reveal key={p.name} delay={i * 0.08}>
          <div
            data-testid={`pricing-${p.name.toLowerCase()}`}
            className={`flex h-full flex-col rounded-lg border p-8 transition-colors ${
              p.featured ? "border-volt bg-volt/[0.05] shadow-[0_30px_80px_-50px_rgba(47,68,232,0.6)]" : "border-slate-200 bg-white hover:border-volt/50"
            }`}
          >
            {p.featured && (
              <span className="mb-4 w-fit bg-volt px-3 py-1 font-mono-p text-[10px] font-bold uppercase tracking-widest text-white">
                Most popular
              </span>
            )}
            <h3 className="font-display text-2xl font-bold text-slate-900">{p.name}</h3>
            <p className="mt-1 font-mono-p text-xs uppercase tracking-wider text-slate-400">{p.tag}</p>
            <div className="mt-6 font-display text-5xl font-black text-slate-900">{p.price}</div>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.blurb}</p>

            <ul className="mt-8 flex-1 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-volt" : "text-slate-400"}`} strokeWidth={2.4} />
                  {f}
                </li>
              ))}
            </ul>

            <button
              data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              onClick={onBookDemo}
              className={`group mt-8 flex items-center justify-center gap-2 py-3.5 font-display font-bold transition-transform hover:-translate-y-0.5 ${
                p.featured ? "bg-volt text-white" : "border border-slate-300 text-slate-900 hover:border-volt hover:text-volt"
              }`}
            >
              Book a Demo
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
            </button>
          </div>
        </Reveal>
      ))}
    </div>
    <p className="mt-8 text-center font-mono-p text-xs uppercase tracking-widest text-slate-400">
      Add only what you need · Modules scale with your operation
    </p>
  </section>
);

export const ROICalculator = () => {
  const [orders, setOrders] = useState([1200]);
  const [errorRate, setErrorRate] = useState([6]);

  const { annualWaste, savings } = useMemo(() => {
    const perError = 22;
    const days = 260;
    const waste = orders[0] * (errorRate[0] / 100) * perError * days;
    return { annualWaste: waste, savings: waste * 0.85 };
  }, [orders, errorRate]);

  const fmt = (n) => "$" + Math.round(n).toLocaleString();

  return (
    <section className="border-y border-slate-200 bg-[#F4F6FB]">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
        <Reveal>
          <Kicker num="09">ROI Calculator</Kicker>
          <SectionHeading className="mt-6">
            What is your warehouse <span className="text-volt">costing you?</span>
          </SectionHeading>
          <p className="mt-6 text-lg leading-relaxed text-slate-500">
            Every mispick, every lost pallet, every wasted hour adds up. Move the sliders
            to estimate what inaccuracy costs your operation each year.
          </p>

          <div className="mt-10 space-y-10">
            <div>
              <div className="mb-4 flex items-baseline justify-between">
                <label className="font-mono-p text-xs uppercase tracking-widest text-slate-500">Order lines / day</label>
                <span className="font-display text-2xl font-bold text-volt">{orders[0].toLocaleString()}</span>
              </div>
              <Slider data-testid="roi-orders" value={orders} onValueChange={setOrders} min={100} max={5000} step={50} />
            </div>
            <div>
              <div className="mb-4 flex items-baseline justify-between">
                <label className="font-mono-p text-xs uppercase tracking-widest text-slate-500">Current error rate</label>
                <span className="font-display text-2xl font-bold text-volt">{errorRate[0]}%</span>
              </div>
              <Slider data-testid="roi-error" value={errorRate} onValueChange={setErrorRate} min={1} max={15} step={1} />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="flex items-center">
          <div className="w-full rounded-lg border border-slate-200 bg-white p-8 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.4)]">
            <div className="font-mono-p text-xs uppercase tracking-widest text-slate-400">Estimated annual cost of errors</div>
            <div className="mt-2 font-display text-5xl font-black text-destructive sm:text-6xl">{fmt(annualWaste)}</div>

            <div className="my-8 h-px bg-slate-200" />

            <div className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-volt">
              <TrendingDown className="h-4 w-4" /> Potential recovery with Stockaris
            </div>
            <div className="mt-2 font-display text-5xl font-black text-volt sm:text-6xl">{fmt(savings)}</div>
            <p className="mt-4 font-mono-p text-[11px] text-slate-400">
              Based on an average 85% reduction in pick errors across Stockaris deployments.
              Illustrative estimate only.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Testimonials = () => (
  <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <Reveal>
      <Kicker num="10">Built from experience</Kicker>
      <SectionHeading className="mt-6 max-w-2xl">
        Made by people who've run the floor.
      </SectionHeading>
      <Link
        to="/resources"
        data-testid="testimonials-resources-link"
        className="group mt-6 inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-volt"
      >
        Read full customer stories
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </Link>
    </Reveal>

    <div className="mt-14 grid gap-4 lg:grid-cols-3">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={t.company} delay={i * 0.08}>
          <motion.div
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="flex h-full flex-col justify-between rounded-lg border border-slate-200 bg-white p-8 transition-colors hover:border-volt/50"
          >
            <p className="font-display text-xl font-medium leading-snug text-slate-900">"{t.quote}"</p>
            <div className="mt-8">
              <div className="inline-block bg-volt px-3 py-1 font-mono-p text-[11px] font-bold uppercase tracking-wider text-white">
                {t.metric}
              </div>
              <div className="mt-4 font-display font-bold text-slate-900">{t.name}</div>
              <div className="font-mono-p text-xs text-slate-400">{t.company}</div>
            </div>
          </motion.div>
        </Reveal>
      ))}
    </div>
  </section>
);
